import type { IconProps } from '@carboncopyco/icons';
import classNames from 'classnames';
import React, { forwardRef } from 'react';
import type { Colors } from '../../constants';
import { TextButton, TextCaption } from '../Typography';

export interface ButtonBaseProps extends React.ComponentPropsWithRef<'button'> {
  id?: string;
  className?: string;
  label?: string;
  disabled: boolean;
  leftIcon?: React.ReactElement<IconProps>;
  rightIcon?: React.ReactElement<IconProps>;
  icon?: React.ReactElement<IconProps>;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  submit?: boolean;
  preventDefault?: boolean;
  textColor?: Colors;
  children?: React.ReactNode;
  cssStyle: string[];
  xSmall?: boolean;
  straightCorners?: ('tl' | 'tr' | 'br' | 'bl')[];
  additionalStyle?: classNames.ArgumentArray;
}

export const ButtonBase = forwardRef(
  (
    {
      id,
      label,
      cssStyle,
      disabled,
      leftIcon,
      rightIcon,
      textColor = 'text-current',
      icon,
      onClick,
      submit,
      preventDefault,
      children,
      xSmall,
      straightCorners,
      additionalStyle = [],
      ...rest
    }: ButtonBaseProps,
    ref: React.Ref<HTMLButtonElement>
  ) => {
    const iconSize = xSmall ? 16 : 20;
    return (
      <button
        id={id}
        ref={ref}
        type={submit ? 'submit' : 'button'}
        onClick={(event) => {
          preventDefault && event.preventDefault();
          preventDefault && event.stopPropagation();
          onClick?.(event);
        }}
        disabled={disabled}
        className={classNames(
          `border-solid flex items-center justify-center rounded-md whitespace-nowrap disabled:cursor-not-allowed focus:outline-none transition duration-300
      ${cssStyle.join(' ')}`,
          {
            '!rounded-ss-none': straightCorners?.includes('tl'),
            '!rounded-se-none': straightCorners?.includes('tr'),
            '!rounded-es-none': straightCorners?.includes('bl'),
            '!rounded-ee-none': straightCorners?.includes('br'),
          },
          ...additionalStyle
        )}
        {...rest}
      >
        {label && (
          <div className="flex justify-center items-center">
            {leftIcon && (
              <div className="pr-1.5 flex items-center">
                {React.cloneElement(leftIcon, {
                  size: iconSize,
                })}
              </div>
            )}
            {xSmall ? (
              <TextCaption
                color={textColor}
                label={label}
              />
            ) : (
              <TextButton
                color={textColor}
                label={label}
              />
            )}

            {rightIcon && (
              <div className="pl-1.5 flex items-center">
                {React.cloneElement(rightIcon, {
                  size: iconSize,
                })}
              </div>
            )}
          </div>
        )}
        {icon && React.cloneElement(icon, { size: iconSize })}
        {children}
      </button>
    );
  }
);

ButtonBase.displayName = 'ButtonBase';
